import { type FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { delay } from "senf-shared";
import { cn } from "../../../util/classNameConstructor";
import Icon from "../../core/icon/Icon";
import InfoToast from "../../molecules/infoToast/InfoToast";

interface MapAccessibilityProps {
  statefulMap: any;
  accessibilityInfotext?: string;
  isMobile?: boolean;
}

const Item = ({
  upperText,
  lowerText,
  icon,
}: {
  upperText: string;
  lowerText: string;
  icon: string;
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2 flex-col text-sm  font-bold">
      <span className="text-text-subdued">{t(upperText)}</span>
      <Icon name={icon as any} className="text-xl w-6 h-6" />
      <span>{t(lowerText)}</span>
    </div>
  );
};

export const MapAccessibility: FC<MapAccessibilityProps> = ({
  statefulMap,
  accessibilityInfotext,
  isMobile,
}) => {
  const { t } = useTranslation();
  const [isMapControlsFocused, setIsMapControlsFocused] = useState(false);
  const [locationDescription, setLocationDescription] = useState("");
  const [drawMode, setDrawMode] = useState<string | null>(null);
  const mapboxControlElements = document.querySelectorAll(
    ".mapboxgl-ctrl, .mapboxgl-ctrl-logo, .mapboxgl-ctrl-group *, .mapboxgl-ctrl-attrib, .mapboxgl-ctrl-attrib-button, .mapboxgl-ctrl-attrib-inner *",
  );

  useEffect(() => {
    if (!statefulMap?.df) return;

    const updateDrawMode = () => {
      setDrawMode(statefulMap.df.getMode());
    };

    // Listen to user interactions that may change draw mode
    document.addEventListener("keydown", updateDrawMode);

    // Also update immediately on mount
    updateDrawMode();

    return () => {
      document.removeEventListener("keydown", updateDrawMode);
    };
  }, [statefulMap?.df]);
  // Handle keyboard navigation and accessibility features
  useEffect(() => {
    if (!accessibilityInfotext) return;

    let lastInteractionWasKeyboard = false;

    const handleKeydown = (event) => {
      if (event.key === "Tab") {
        lastInteractionWasKeyboard = true;
      }
    };

    const handleMousedown = () => {
      lastInteractionWasKeyboard = false;
    };
    const handleKeyDown = async (e) => {
      if (!statefulMap) return;

      let movement = "";
      let locationDescription = "";

      const descriptionElement = document.getElementById(
        "location-description",
      );

      const updateLocationDescription = async () => {
        const mapCenter = statefulMap.getCenter();
        const mapPoint = { lng: mapCenter.lng, lat: mapCenter.lat };

        try {
          const response = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${
              mapPoint.lng
            },${mapPoint.lat}.json?access_token=${
              import.meta.env.VITE_MAPBOX_ACCESS_TOKEN
            }`,
          );
          const data = await response.json();
          const { features } = data;

          let city = "";
          let district = "";
          let address = "";

          features.forEach((feature) => {
            if (feature.place_type.includes("place")) {
              city = feature.text;
            } else if (feature.place_type.includes("district")) {
              district = feature.text;
            } else if (feature.place_type.includes("address")) {
              address = feature.text;
            }
          });

          await delay(1000);

          if (city)
            locationDescription +=
              t("accessibility:map.location.city", { city }) + ". ";
          if (district)
            locationDescription +=
              t("accessibility:map.location.district", { district }) + ". ";
          if (address)
            locationDescription +=
              t("accessibility:map.location.address", { address }) + ". ";

          if (!locationDescription) {
            locationDescription = t("accessibility:map.location.notAvailable");
          }

          if (descriptionElement) {
            const fullDescription = t("accessibility:map.location.youAreAt", {
              location: locationDescription.trim(),
            });
            descriptionElement.innerText = fullDescription;
            setLocationDescription(fullDescription);
          }
        } catch (error) {
          console.error("Error fetching location description:", error);
        }
      };

      const handleMovement = async (movement) => {
        if (descriptionElement) {
          descriptionElement.innerText = movement;
          setLocationDescription(movement);
        }
        await updateLocationDescription();
      };

      switch (e.key) {
        case "ArrowUp":
          movement = t("accessibility:map.movement.north");
          await handleMovement(movement);
          break;
        case "ArrowDown":
          movement = t("accessibility:map.movement.south");
          await handleMovement(movement);
          break;
        case "ArrowLeft":
          movement = t("accessibility:map.movement.west");
          await handleMovement(movement);
          break;
        case "ArrowRight":
          movement = t("accessibility:map.movement.east");
          await handleMovement(movement);
          break;
        case "+":
          movement = t("accessibility:map.movement.zoomIn");
          await handleMovement(movement);
          break;
        case "-":
          movement = t("accessibility:map.movement.zoomOut");
          await handleMovement(movement);
          break;
        case " ": {
          await updateLocationDescription();
          const pointType =
            statefulMap?.df?.getMode() === "custom_draw_line_string"
              ? t("accessibility:map.drawing.pathPoint")
              : statefulMap?.df?.getMode() === "custom_draw_polygon"
                ? t("accessibility:map.drawing.cornerPoint")
                : t("accessibility:map.drawing.point");

          movement = t("accessibility:map.drawing.addedPoint", {
            type: pointType,
            location: locationDescription.trim(),
          });

          if (descriptionElement) {
            descriptionElement.innerText = movement;
            setLocationDescription(movement);
          }
          break;
        }
        case "Enter":
          movement = t("accessibility:map.drawing.finished");
          if (descriptionElement) {
            descriptionElement.innerText = movement;
            setLocationDescription(movement);
          }
          break;
        case "Escape":
          movement = t("accessibility:map.drawing.canceled");
          if (descriptionElement) {
            descriptionElement.innerText = movement;
            setLocationDescription(movement);
          }
          break;
        case "Backspace":
          movement = t("accessibility:map.drawing.removedPoint");
          if (descriptionElement) {
            descriptionElement.innerText = movement;
            setLocationDescription(movement);
          }
          break;
        default:
          return;
      }

      e.preventDefault();
    };

    const handleFocus = async (event) => {
      if (lastInteractionWasKeyboard) {
        document.addEventListener("keydown", handleKeyDown);
      }
    };
    const handleBlur = () => {
      // Handle blur if needed
      document.removeEventListener("keydown", handleKeyDown);
    };

    const mapElement = document.getElementsByClassName("mapboxgl-canvas")[0];

    if (mapElement) {
      document.addEventListener("keydown", handleKeydown);
      document.addEventListener("mousedown", handleMousedown);
      mapElement.addEventListener("focus", handleFocus);
      mapElement.addEventListener("blur", handleBlur);
    }

    return () => {
      document.removeEventListener("keydown", handleKeydown);
      document.removeEventListener("mousedown", handleMousedown);
      if (mapElement) {
        mapElement.removeEventListener("focus", handleFocus);
        mapElement.removeEventListener("blur", handleBlur);
      }
    };
  }, [statefulMap, t]);

  // Handle focus state for accessibility UI
  useEffect(() => {
    if (!accessibilityInfotext) return;

    const handleFocus = (event) => {
      if (event.target.matches(".mapboxgl-canvas:focus-visible")) {
        mapboxControlElements.forEach((element) => {
          element.style.opacity = 0;
          element.style.pointerEvents = "none";
        });
        setIsMapControlsFocused(true);
      }
    };

    const handleBlur = () => {
      setIsMapControlsFocused(false);
      mapboxControlElements.forEach((element) => {
        element.style.opacity = 1;
        element.style.pointerEvents = "all";
      });
    };

    const mapElement = document.getElementsByClassName("mapboxgl-canvas")[0];

    if (mapElement) {
      mapElement.addEventListener("focus", handleFocus);
      mapElement.addEventListener("blur", handleBlur);
    }

    return () => {
      if (mapElement) {
        mapElement.removeEventListener("focus", handleFocus);
        mapElement.removeEventListener("blur", handleBlur);
      }
    };
  }, [accessibilityInfotext, statefulMap?.loaded]);

  if (!isMapControlsFocused) return null;

  return (
    <div
      className={cn(
        "pointer-events-none absolute z-50 flex items-center justify-center",
        isMobile ? "left-0 h-[calc(100%_-_150px)] w-screen" : "h-full w-full",
      )}
    >
      <div
        className="z-50 flex h-56 w-56 items-center justify-center border-[3px] border-black"
        tabIndex={-1}
        aria-label={t("accessibility:map.controls")}
      >
        <Icon name="plus-md" />
      </div>

      <div
        className="z-60 pointer-events-auto absolute top-[calc(50vh_+_160px)] flex w-[400px] flex-col gap-3 rounded-xl bg-white p-5 shadow-xl border"
        aria-live="polite"
        style={{ display: locationDescription ? "flex" : "none" }}
      >
        <div id="location-description" aria-live="assertive">
          {locationDescription}
        </div>
      </div>
      <div className="z-60 pointer-events-auto fixed bottom-5 flex w-auto gap-5 rounded-xl bg-white p-3 shadow-xl border">
        <Item
          upperText="accessibility:map.arrowKeys"
          icon="arrow-keys"
          lowerText="accessibility:map.navigate"
        />
        <Item
          upperText="accessibility:map.zoomKeys"
          icon="zoom-keys"
          lowerText="accessibility:map.adjustZoom"
        />

        {drawMode === "custom_draw_point" ||
        drawMode === "custom_draw_line_string" ||
        drawMode === "custom_draw_polygon" ? (
          <>
            <Item
              upperText="accessibility:map.spaceKey"
              icon="key-space"
              lowerText="accessibility:map.setPoint"
            />
            {(drawMode === "custom_draw_line_string" ||
              drawMode === "custom_draw_polygon") && (
              <>
                <Item
                  upperText="accessibility:map.enterKey"
                  icon="key-enter"
                  lowerText="accessibility:map.finishInput"
                />
                <Item
                  upperText="accessibility:map.backspaceKey"
                  icon="key-delete"
                  lowerText="accessibility:map.undo"
                />
                <Item
                  upperText="accessibility:map.escKey"
                  icon="key-esc"
                  lowerText="accessibility:map.cancel"
                />
              </>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};
